// NEW: isMicroappsEnabled + uuid still needed by updateAccessToken (refresh flow); jsonApiClient/unsecureApiClient still needed by signup/signIn/doLogout
// NEW: getAccessToken + sendOtp now use raw fetch to VUE_APP_LOGIN_CODE_AUTH_URL (separate auth service) — no Basic auth
import { isMicroappsEnabled, uuid } from '@/lib/utils';
import { jsonApiClient, unsecureApiClient } from './fetch_helper';
import { fetchHandler } from './utils';

/**
 * Every endpoint in this file is part of the login surface. The auth service
 * answers 401 for a wrong or expired otp, an unusable login code or a rejected
 * id token - none of which is a session going stale - so the global logout is
 * held back here and each caller keeps showing its own message.
 */
const skipAuthFailureLogout = (response: any) =>
  fetchHandler(response, { handleAuthFailure: false });

// OLD: { phone, purpose, token }
// NEW: { phoneNumber, captchaToken, deviceType? } — matches new auth service SendOtpRequest schema
interface SendOtpModel {
  phoneNumber: string | number;
  captchaToken: any;
  deviceType?: string;
}
/**
 * POST Posts data of customer for sign up
 * Mandatory { clientId, clientSecret, phone, otp}
 * @param {Object} data
 * @returns {Promise}
 */
export function signup(data: object): Promise<any> {
  return unsecureApiClient.fetch('/v1/customer/signup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(data),
  });
}

/**
 * POST Signs in customer (Access Token is mandatory)
 * @returns {Promise}
 */
export function signIn(): Promise<any> {
  return jsonApiClient.fetch('/v1/customer/login', {
    method: 'POST',
  });
}

// OLD: POST /v1/otp/send on VUE_APP_API_BASE_URL via unsecureApiClient — body: { phone, purpose, token }
// OLD: /**
// OLD:  * POST Send OTP for a signup to customer's registered phone number
// OLD:  * @param {Object} context
// OLD:  * @param {String} context.phone
// OLD:  * @param {String} context.purpose
// OLD:  * @param {String} context.token
// OLD:  * @returns {Promise}
// OLD:  */
// OLD: export function sendOtp({
// OLD:   phone,
// OLD:   purpose = 'SIGNUP',
// OLD:   token,
// OLD: }: SendOtpModel): Promise<any> {
// OLD:   return unsecureApiClient.post('/v1/otp/send', { phone, purpose, token });
// OLD: }

// NEW: POST /api/v1/auth/otp on VUE_APP_LOGIN_CODE_AUTH_URL (auth microservice) via raw fetch
// NEW: Different domain — cannot use unsecureApiClient (bound to VUE_APP_API_BASE_URL)
// NEW: Field renames: phone→phoneNumber, token→captchaToken; purpose removed; deviceType:'WEB' added
// NEW: Error shape: old { data.error.code === 477, data.error.message } → new { success, message }
export function sendOtp({
  phoneNumber,
  captchaToken,
  deviceType = 'WEB',
}: SendOtpModel): Promise<any> {
  return fetch(`${process.env.VUE_APP_LOGIN_CODE_AUTH_URL}/api/v1/auth/otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ phoneNumber, deviceType, captchaToken }),
  }).then(skipAuthFailureLogout);
}

/**
 * POST Get access token for the corresponding id token from google
 * Used for all subsequent requests
 * @param {String} userName
 * @param {String} password
 * @param {String} authType
 * @param {Number} authVersion
 * @returns {Promise}
 */
// OLD: all paths (web + GPAY) used POST /oauth/token on VUE_APP_API_BASE_URL with Basic auth + form-encoded body
// OLD: export function getAccessToken(
// OLD:   userName: string | number,
// OLD:   password: string | number,
// OLD:   authType = '',
// OLD:   authVersion = 2
// OLD: ): Promise<any> {
// OLD:   const formData = `grant_type=password&username=${userName}&scope=read+write&password=${password}&auth_type=${authType || 'GMAIL'}&auth_version=${authVersion}`;
// OLD:   let authHeader = '';
// OLD:   if (isMicroappsEnabled()) {
// OLD:     authHeader = btoa(`${process.env.VUE_APP_GPAY_APP_ID}:${process.env.VUE_APP_GPAY_APP_KEY}`);
// OLD:   } else {
// OLD:     authHeader = btoa(`${process.env.VUE_APP_ID}:${process.env.VUE_APP_KEY}`);
// OLD:   }
// OLD:   return fetch(`${process.env.VUE_APP_API_BASE_URL}/oauth/token`, {
// OLD:     method: 'POST',
// OLD:     headers: { Authorization: `Basic ${authHeader}`, Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8', 'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh', 'mt-request-id': uuid() },
// OLD:     body: formData,
// OLD:   }).then(fetchHandler);
// OLD: }

// NEW: GPAY has no new auth service endpoint yet (confirmed by backend team 2026-06-11) — stays on old /oauth/token with Basic auth
// NEW: Web (OTP + GMAIL) migrated to POST /api/v1/auth/login/web — JSON body, no Basic auth
// NEW: authVersion param kept for signature compat but no longer sent on web path
export function getAccessToken(
  userName: string | number,
  password: string | number,
  authType = 'OTP',
  authVersion = 2
): Promise<any> {
  // NEW: GPAY path — no auth service endpoint yet; keep old /oauth/token with Basic auth unchanged
  if (isMicroappsEnabled()) {
    const formData = `grant_type=password&username=${userName}&scope=read+write&password=${password}&auth_type=${
      authType || 'GMAIL'
    }&auth_version=${authVersion}`;
    const authHeader = btoa(
      `${process.env.VUE_APP_GPAY_APP_ID}:${process.env.VUE_APP_GPAY_APP_KEY}`
    );
    return fetch(`${process.env.VUE_APP_API_BASE_URL}/oauth/token`, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${authHeader}`,
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
        'mt-request-id': uuid(),
      },
      body: formData,
    }).then(skipAuthFailureLogout);
  }
  // NEW: Web path — JSON body, no Basic auth; GMAIL uses idToken, OTP uses phoneNumber + otp
  // NEW: captchaToken not required for GMAIL web login (confirmed from backend 2026-06-11)
  const body: Record<string, any> = { authType: authType || 'OTP' };
  if (authType === 'GMAIL') {
    // NEW: GMAIL credential (Google JWT) maps to idToken field in new schema
    body.idToken = password;
  } else {
    // NEW: OTP login — userName is phone number, password is OTP value
    body.phoneNumber = String(userName);
    body.otp = String(password);
  }
  return fetch(
    `${process.env.VUE_APP_LOGIN_CODE_AUTH_URL}/api/v1/auth/login/web`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }
  ).then(skipAuthFailureLogout);
}

/**
 * POST Get access token for refresh token
 * Used for all subsequent requests
 * @param {String} refreshToken
 * @returns {Promise}
 */
export function updateAccessToken(refreshToken: string): Promise<any> {
  const formData = `grant_type=refresh_token&refresh_token=${refreshToken}&scope=read+write&auth_type=GMAIL`;
  let authHeader = '';
  if (isMicroappsEnabled()) {
    authHeader = btoa(
      `${process.env.VUE_APP_GPAY_APP_ID}:${process.env.VUE_APP_GPAY_APP_KEY}`
    );
  } else {
    authHeader = btoa(`${process.env.VUE_APP_ID}:${process.env.VUE_APP_KEY}`);
  }
  return fetch(`${process.env.VUE_APP_API_BASE_URL}/oauth/token`, {
    method: 'POST',
    headers: {
      Authorization: `Basic ${authHeader}`,
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh',
      'mt-request-id': uuid(),
    },
    body: formData,
  }).then(skipAuthFailureLogout);
}

// NEW: links the new auth service JWT with the Freo backend customer record
// NEW: must be called after login/web — without it customerData returns customerExists:false and consent fails
// OLD: endpoint pointed at demo.moneytap.com (VUE_APP_DEMO_API_URL) — separate domain from main backend
// NEW: endpoint is on the main backend base URL (VUE_APP_API_BASE_URL) — same host we always used for Freo backend calls; raw fetch still used because jsonApiClient auto-adds the stored Bearer, but here we need the freshly-issued auth-service token
// NEW: accessToken passed as param (already stored in auth at call site) — avoids importing store into login_utils
/**
 * POST Link new auth service JWT to Freo backend customer record
 * @param {String} accessToken - new auth service JWT received from /api/v1/auth/login/web
 * @returns {Promise}
 */
export function linkCustomer(accessToken: string): Promise<any> {
  return fetch(
    // OLD: `${process.env.VUE_APP_DEMO_API_URL}/v1/customer/link-customer` — demo.moneytap.com
    // NEW: hit link-customer on the standard backend base URL instead of the demo host
    `${process.env.VUE_APP_API_BASE_URL}/v1/customer/link-customer`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
        // OLD: 'app-platform': 'ooneipiDei0Tequ0Thaepheech8ietoh' — backend confirmed not required on this endpoint
        'mt-request-id': uuid(),
      },
    }
  ).then(skipAuthFailureLogout);
}

/**
 * POST Logout logged in customer
 * @returns {Promise}
 */
export function doLogout(): Promise<any> {
  return jsonApiClient.fetch('/logout', {
    method: 'POST',
  });
}

/**
 * POST Exchange a short-lived login code (from SMS link) for an access token
 * @param {String} loginCode - One-time login code extracted from /login-code/:code URL
 * @returns {Promise} resolves with { accessToken }
 */
// Login-via-code flow — user clicks SMS link → /login-code/:code → this call → access token
// Uses native fetch (not unsecureApiClient) because VUE_APP_LOGIN_CODE_AUTH_URL is a separate auth service domain
// AbortController timeout: auth.freo.money could hang; without this the spinner stays forever
export function validateLoginCode(loginCode: string): Promise<any> {
  const controller = new AbortController();
  // 10s timeout; AbortController cancels the fetch and throws, caught by LoginCode.vue
  // error handler — without this, a hung auth service leaves the page stuck on
  // "Validating code..." indefinitely
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  return fetch(
    `${process.env.VUE_APP_LOGIN_CODE_AUTH_URL}/api/v1/auth/login-code/token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ loginCode }),
      signal: controller.signal,
    }
  )
    .then(skipAuthFailureLogout)
    .finally(() => clearTimeout(timeoutId));
}
